import { useState } from 'react'
import ReactDOM from 'react-dom'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader, PageContainer } from '@/components/layout/PageHeader'
import { Button, Badge, Avatar, Card, CardContent } from '@/components/ui'
import { adminApi } from '@/modules/admin/api/admin.api'
import { hrApi } from '@/modules/hr/api/hr.api'
import { formatDateTime } from '@/lib/formatters'
import { ROUTES } from '@/router/routes'

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'info' | 'roles' | 'employee' | 'sessions'>('info')
  const [showRoleModal, setShowRoleModal] = useState(false)

  const { data: user, isLoading } = useQuery({
    queryKey: ['admin-user', id],
    queryFn: () => adminApi.getUserById(id!),
    enabled: !!id,
  })

  const { data: sessions } = useQuery({
    queryKey: ['admin-user-sessions', id],
    queryFn: () => adminApi.getUserSessions(id!),
    enabled: !!id && activeTab === 'sessions',
  })

  const toggleStatus = useMutation({
    mutationFn: () => user?.isActive ? adminApi.deactivateUser(id!) : adminApi.activateUser(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-user', id] }),
  })

  const removeRole = useMutation({
    mutationFn: (roleId: string) => adminApi.removeRoleFromUser(id!, roleId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-user', id] }),
  })

  const revokeSession = useMutation({
    mutationFn: (sessionId: string) => adminApi.revokeSession(id!, sessionId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-user-sessions', id] }),
  })

  // ⚠ Query ici — AVANT les return conditionnels (règle des hooks React)
  const { data: linkedEmployee } = useQuery({
    queryKey: ['user-linked-employee', id],
    queryFn: async () => {
      try {
        const res = await hrApi.employees.list({ limit: 100 })
        const list = (res as any).employees ?? (res as any).data ?? []
        return list.find((e: any) => e.userId === id) ?? null
      } catch { return null }
    },
    enabled: !!id,
  })

  if (isLoading) return (
    <PageContainer>
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--blue)] border-t-transparent rounded-full" />
      </div>
    </PageContainer>
  )

  if (!user) return (
    <PageContainer>
      <div className="text-center py-10 text-[var(--tx3)]">Utilisateur introuvable.</div>
    </PageContainer>
  )

  const TABS = [
    { key: 'info',     label: 'Informations' },
    { key: 'roles',    label: `Rôles (${user.userRoles?.length ?? 0})` },
    { key: 'employee', label: linkedEmployee ? '👤 Fiche employé' : 'Fiche employé' },
    { key: 'sessions', label: 'Sessions' },
  ] as const

  return (
    <PageContainer>
      <PageHeader
        title={`${user.firstName} ${user.lastName}`}
        description={user.email}
        breadcrumb={[
          { label: 'Administration' },
          { label: 'Utilisateurs', href: ROUTES.ADMIN_USERS },
          { label: `${user.firstName} ${user.lastName}` },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => toggleStatus.mutate()}
              isLoading={toggleStatus.isPending}
              className={user.isActive ? 'text-[var(--red-text)] border-red-400/30 hover:border-red-400' : 'text-[var(--green-text)] border-green-400/30 hover:border-green-400'}
            >
              {user.isActive ? 'Désactiver' : 'Activer'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(ROUTES.ADMIN_USERS)}>
              ← Retour
            </Button>
          </div>
        }
      />

      {/* Header card */}
      <Card className="mb-6">
        <CardContent className="py-5">
          <div className="flex items-center gap-4">
            <Avatar firstName={user.firstName} lastName={user.lastName} size="lg" />
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-sm font-semibold text-[var(--tx2)]">{user.firstName} {user.lastName}</h2>
                <Badge variant={user.isActive ? 'success' : 'default'}>{user.isActive ? 'Actif' : 'Inactif'}</Badge>
                <Badge variant={user.twoFactorEnabled ? 'success' : 'warning'} size="sm">
                  2FA {user.twoFactorEnabled ? 'activé' : 'désactivé'}
                </Badge>
                {linkedEmployee && (
                  <button
                    onClick={() => setActiveTab('employee')}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--blue-bg)] text-[var(--blue-d)] border border-[rgba(245,124,0,0.25)] hover:bg-[#BAE6FD] transition-colors"
                  >
                    👤 {linkedEmployee.matricule}
                  </button>
                )}
              </div>
              <p className="text-[var(--tx3)] text-sm mt-0.5">{user.email}</p>
            </div>
            <div className="text-right text-xs text-[var(--tx3)]">
              <p>Créé le {formatDateTime(user.createdAt)}</p>
              {user.lastLoginAt && <p>Dernière connexion {formatDateTime(user.lastLoginAt)}</p>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-[var(--bd)]">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'text-[var(--blue-d)] border-blue-400'
                : 'text-[var(--tx3)] border-transparent hover:text-[var(--tx)]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab: Informations */}
      {activeTab === 'info' && (
        <div className="grid grid-cols-2 gap-6">
          <Card>
            <CardContent className="py-5">
              <h3 className="text-sm font-semibold text-[var(--tx2)] mb-4">Informations personnelles</h3>
              <dl className="space-y-3">
                {[
                  { label: 'Prénom', value: user.firstName },
                  { label: 'Nom', value: user.lastName },
                  { label: 'Email', value: user.email },
                  { label: 'Identifiant', value: <span className="font-mono text-xs">{user.id}</span> },
                ].map(({ label, value }) => (
                  <div key={label} className="flex justify-between items-start">
                    <dt className="text-xs text-[var(--tx3)]">{label}</dt>
                    <dd className="text-sm text-[var(--tx2)] text-right">{value}</dd>
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="py-5">
              <h3 className="text-sm font-semibold text-[var(--tx2)] mb-4">Sécurité</h3>
              <dl className="space-y-3">
                {[
                  { label: 'Statut compte', value: <Badge variant={user.isActive ? 'success' : 'default'} size="sm">{user.isActive ? 'Actif' : 'Inactif'}</Badge> },
                  { label: 'Authentification 2FA', value: <Badge variant={user.twoFactorEnabled ? 'success' : 'warning'} size="sm">{user.twoFactorEnabled ? 'Activée' : 'Désactivée'}</Badge> },
                  { label: 'Créé le', value: formatDateTime(user.createdAt) },
                  { label: 'Dernière connexion', value: user.lastLoginAt ? formatDateTime(user.lastLoginAt) : '—' },
                ].map(({ label, value }) => (
                  <div key={label} className="flex justify-between items-center">
                    <dt className="text-xs text-[var(--tx3)]">{label}</dt>
                    <dd className="text-sm text-[var(--tx2)]">{value}</dd>
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tab: Rôles */}
      {activeTab === 'roles' && (
        <Card>
          <CardContent className="p-0">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--bd)]">
              <h3 className="text-sm font-semibold text-[var(--tx2)]">Rôles assignés</h3>
              <Button size="sm" onClick={() => setShowRoleModal(true)}>+ Assigner un rôle</Button>
            </div>
            {(!user.userRoles || user.userRoles.length === 0) ? (
              <div className="py-12 text-center text-[var(--tx3)] text-sm">Aucun rôle assigné</div>
            ) : (
              <div className="divide-y divide-[var(--bd)]">
                {user.userRoles?.map((ur: any) => (
                  <div key={ur.roleId} className="flex items-center justify-between px-5 py-3">
                    <div>
                      <p className="text-sm font-medium text-[var(--tx)]">{ur.role?.name}</p>
                      {ur.role?.description && <p className="text-xs text-[var(--tx3)]">{ur.role.description}</p>}
                    </div>
                    <div className="flex items-center gap-3">
                      {ur.role?.isSystem && <Badge variant="purple" size="sm">Système</Badge>}
                      {!ur.role?.isSystem && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-[var(--red-text)] hover:text-red-300"
                          onClick={() => removeRole.mutate(ur.roleId)}
                          isLoading={removeRole.isPending}
                        >
                          Retirer
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab: Fiche employé */}
      {activeTab === 'employee' && (
        <div className="space-y-4">
          {linkedEmployee ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="py-5">
                    <h3 className="text-sm font-semibold text-[var(--tx2)] mb-4">Informations RH</h3>
                    <dl className="space-y-3">
                      {[
                        { label: 'Matricule',       value: <span className="font-mono text-xs font-bold text-[var(--blue-d)]">{linkedEmployee.matricule}</span> },
                        { label: 'Poste',           value: linkedEmployee.jobTitle ?? '—' },
                        { label: 'Département',     value: linkedEmployee.department?.name ?? '—' },
                        { label: "Date d'embauche", value: linkedEmployee.hireDate ? new Date(linkedEmployee.hireDate).toLocaleDateString('fr-FR') : '—' },
                        { label: 'Statut',          value: <Badge variant={linkedEmployee.status === 'ACTIVE' ? 'success' : 'default'} size="sm">{linkedEmployee.status}</Badge> },
                        { label: 'N° IPRES',        value: linkedEmployee.ipresId ?? '—' },
                      ].map(({ label, value }) => (
                        <div key={label} className="flex justify-between items-center">
                          <dt className="text-xs text-[var(--tx3)]">{label}</dt>
                          <dd className="text-sm text-[var(--tx2)]">{value}</dd>
                        </div>
                      ))}
                    </dl>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="py-5">
                    <h3 className="text-sm font-semibold text-[var(--tx2)] mb-4">Contrat actif</h3>
                    {linkedEmployee.activeContract ? (
                      <dl className="space-y-3">
                        {[
                          { label: 'Type',          value: linkedEmployee.activeContract.type },
                          { label: 'Salaire brut',  value: `${linkedEmployee.activeContract.grossSalary?.toLocaleString('fr-FR') ?? '—'} XOF` },
                          { label: 'Début',         value: linkedEmployee.activeContract.startDate ? new Date(linkedEmployee.activeContract.startDate).toLocaleDateString('fr-FR') : '—' },
                          { label: 'Fin',           value: linkedEmployee.activeContract.endDate ? new Date(linkedEmployee.activeContract.endDate).toLocaleDateString('fr-FR') : 'CDI' },
                        ].map(({ label, value }) => (
                          <div key={label} className="flex justify-between items-center">
                            <dt className="text-xs text-[var(--tx3)]">{label}</dt>
                            <dd className="text-sm text-[var(--tx2)]">{value}</dd>
                          </div>
                        ))}
                      </dl>
                    ) : (
                      <div className="py-8 text-center text-[var(--tx3)] text-sm">Aucun contrat actif</div>
                    )}
                  </CardContent>
                </Card>
              </div>
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.location.href = `/hr/employees/${linkedEmployee.id}`}
                >
                  Ouvrir la fiche complète →
                </Button>
              </div>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center space-y-4">
                <div style={{ width:56, height:56, borderRadius:12, background:'var(--surface-2)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto', fontSize:24 }}>👤</div>
                <div>
                  <p className="text-sm font-semibold text-[var(--tx2)]">Aucune fiche employé liée</p>
                  <p className="text-xs text-[var(--tx3)] mt-1">Cet utilisateur n'a pas encore de profil RH associé.</p>
                </div>
                <Button
                  size="sm"
                  onClick={() => window.location.href = '/hr/employees/new'}
                >
                  + Créer la fiche employé
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Tab: Sessions */}
      {activeTab === 'sessions' && (
        <Card>
          <CardContent className="p-0">
            <div className="px-5 py-4 border-b border-[var(--bd)]">
              <h3 className="text-sm font-semibold text-[var(--tx2)]">Sessions actives</h3>
            </div>
            {(!sessions || sessions.length === 0) ? (
              <div className="py-12 text-center text-[var(--tx3)] text-sm">Aucune session active</div>
            ) : (
              <div className="divide-y divide-[var(--bd)]">
                {sessions.map((session: any) => (
                  <div key={session.id} className="flex items-center justify-between px-5 py-3">
                    <div>
                      <p className="text-xs font-mono text-[var(--tx2)]">{session.ipAddress ?? 'IP inconnue'}</p>
                      <p className="text-xs text-[var(--tx3)] mt-0.5">{session.userAgent ?? 'Agent inconnu'}</p>
                      <p className="text-xs text-[var(--tx4)] mt-0.5">Créée {formatDateTime(session.createdAt)}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-[var(--red-text)] hover:text-red-300"
                      onClick={() => revokeSession.mutate(session.id)}
                      isLoading={revokeSession.isPending}
                    >
                      Révoquer
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Modal assigner rôle */}
      {showRoleModal && (
        <AssignRoleModal userId={id!} onClose={() => { setShowRoleModal(false); queryClient.invalidateQueries({ queryKey: ['admin-user', id] }) }} />
      )}
    </PageContainer>
  )
}

function AssignRoleModal({ userId, onClose }: { userId: string; onClose: () => void }) {
  const [selectedRoleId, setSelectedRoleId] = useState('')
  const [error, setError] = useState('')

  const { data: rolesData } = useQuery({
    queryKey: ['admin-roles-list'],
    queryFn: () => adminApi.getRoles({ page: 1, limit: 100 }),
  })

  const assign = useMutation({
    mutationFn: () => adminApi.assignRoleToUser(userId, selectedRoleId),
    onSuccess: onClose,
    onError: (err: any) => setError(err?.response?.data?.message ?? 'Erreur'),
  })

  return (
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm z-[99999] flex items-center justify-center p-4">
      <div className="nx-card w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--bd)]">
          <h2 className="text-base font-semibold text-[var(--tx)]">Assigner un rôle</h2>
          <button onClick={onClose} className="text-[var(--tx3)] hover:text-[var(--tx)]">✕</button>
        </div>
        <div className="p-6 space-y-4">
          {error && <div className="bg-[var(--red-bg)] border border-[rgba(239,68,68,0.25)] rounded-lg p-3 text-sm text-[var(--red-text)]">{error}</div>}
          <div>
            <label className="block text-xs font-medium text-[var(--tx3)] mb-1">Rôle *</label>
            <select
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              className="w-full bg-white border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] focus:outline-none focus:border-blue-500"
            >
              <option value="">Sélectionner un rôle...</option>
              {rolesData?.data.map((role: any) => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-[var(--bd)]">
          <Button variant="outline" className="flex-1" onClick={onClose}>Annuler</Button>
          <Button className="flex-1" onClick={() => assign.mutate()} isLoading={assign.isPending} disabled={!selectedRoleId}>
            Assigner
          </Button>
        </div>
      </div>
    </div>
  )
}
